import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useSales, useCreateSale, useDeleteSale } from '@/hooks/usePSI';
import {
  useSalesQuotations,
  useSalesOrders,
  useDeleteSalesQuotation,
  useDeleteSalesOrder,
  useUpdateSQStatus,
  useUpdateSOStatus,
  useAcceptQuotation,
} from '@/hooks/useSalesOrder';
import { ISale, ISaleCreateRequest } from '@/types/psi';
import {
  SQStatus,
  SOStatus,
  SQ_STATUS_LABELS,
  SO_STATUS_LABELS,
  SQ_STATUS_COLORS,
  SO_STATUS_COLORS,
  SQResponse,
  SOResponse,
} from '@/types/salesorder';
import { formatCurrency, formatDate, isQuotationExpired, getDaysUntilExpiry } from '@/services/salesService';
import SearchBar from '@/components/SearchBar';
import Pagination from '@/components/Pagination';
import LoadingSpinner from '@/components/LoadingSpinner';
import SalesQuotationDialog from './SalesQuotationDialog';
import SalesOrderDialog from './SalesOrderDialog';
import { MobileCard } from '@/components/ui';

type TabType = 'quotations' | 'orders' | 'delivery';

const SalesList: React.FC = () => {
  const { t } = useTranslation('psi');
  const { subtab } = useParams<{ subtab?: string }>();
  const navigate = useNavigate();

  // Determine active tab from URL subtab parameter, default to quotations
  const getActiveTab = (): TabType => {
    if (subtab === 'quotations' || subtab === 'orders' || subtab === 'delivery') {
      return subtab;
    }
    return 'quotations';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getActiveTab());

  // Sync activeTab with URL changes
  useEffect(() => {
    const newTab = getActiveTab();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [subtab]);

  // Handle tab change - navigate to update URL
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    navigate(`/app/psi/sales/${tab}`);
  };

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [showSQDialog, setShowSQDialog] = useState(false);
  const [showSODialog, setShowSODialog] = useState(false);
  const [editingSQ, setEditingSQ] = useState<SQResponse | null>(null);
  const [editingSO, setEditingSO] = useState<SOResponse | null>(null);
  const [newSale, setNewSale] = useState<Partial<ISaleCreateRequest>>({
    date: new Date().toISOString().split('T')[0],
    status: 'pending',
  });

  // Sheet-based sales (existing)
  const { data, isLoading, refetch } = useSales({ page, pageSize, q: searchQuery });
  const createMutation = useCreateSale();
  const deleteMutation = useDeleteSale();

  // Sales Quotations
  const { data: sqData, isLoading: sqLoading, refetch: sqRefetch } = useSalesQuotations();
  const deleteSQMutation = useDeleteSalesQuotation();
  const updateSQStatusMutation = useUpdateSQStatus();
  const acceptQuotationMutation = useAcceptQuotation();

  // Sales Orders
  const { data: soData, isLoading: soLoading, refetch: soRefetch } = useSalesOrders();
  const deleteSOMutation = useDeleteSalesOrder();
  const updateSOStatusMutation = useUpdateSOStatus();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleAddNew = async () => {
    if (!newSale.customer || !newSale.item) {
      alert(t('validation.customerAndItemRequired'));
      return;
    }

    try {
      await createMutation.mutateAsync(newSale as ISaleCreateRequest);
      setIsAddingNew(false);
      setNewSale({ date: new Date().toISOString().split('T')[0], status: 'pending' });
    } catch (error) {
      logger.error('Failed to create sale:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm(t('sales.confirmDelete'))) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        logger.error('Failed to delete sale:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  // SQ handlers
  const handleDeleteSQ = async (id: string) => {
    if (confirm('Are you sure you want to delete this quotation?')) {
      try {
        await deleteSQMutation.mutateAsync(id);
        toast.success('Quotation deleted');
      } catch (error) {
        toast.error(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handleUpdateSQStatus = async (id: string, status: SQStatus) => {
    try {
      await updateSQStatusMutation.mutateAsync({ id, status });
      toast.success(`Status updated to ${SQ_STATUS_LABELS[status]}`);
    } catch (error) {
      toast.error(`Update failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleAcceptQuotation = async (sq: SQResponse) => {
    if (isQuotationExpired(sq.valid_until)) {
      toast.error('This quotation has expired and cannot be accepted');
      return;
    }

    if (sq.status === 'accepted') {
      toast.error('This quotation has already been accepted');
      return;
    }

    if (confirm('Accept this quotation and create a Sales Order?')) {
      try {
        const result = await acceptQuotationMutation.mutateAsync(sq.sq_id);
        toast.success(`Sales Order ${result.so_number} created from quotation`);
        if (result.sheet_url) {
          toast.success(
            <span>
              SO sheet generated!{' '}
              <a href={result.sheet_url} target="_blank" rel="noopener noreferrer" className="underline">
                Open
              </a>
            </span>,
            { duration: 5000 }
          );
        }
        handleTabChange('orders');
        soRefetch();
      } catch (error) {
        toast.error(`Accept failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  // SO handlers
  const handleDeleteSO = async (id: string) => {
    if (confirm('Are you sure you want to delete this order?')) {
      try {
        await deleteSOMutation.mutateAsync(id);
        toast.success('Order deleted');
      } catch (error) {
        toast.error(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handleUpdateSOStatus = async (id: string, status: SOStatus) => {
    try {
      await updateSOStatusMutation.mutateAsync({ id, status });
      toast.success(`Status updated to ${SO_STATUS_LABELS[status]}`);
    } catch (error) {
      toast.error(`Update failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const currentLoading = activeTab === 'quotations' ? sqLoading : activeTab === 'orders' ? soLoading : isLoading;
  if (currentLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Tabs - mobile scrollable */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex overflow-x-auto scrollbar-hide snap-x snap-mandatory">
          <button
            onClick={() => handleTabChange('quotations')}
            className={`snap-start flex-shrink-0 py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] ${
              activeTab === 'quotations'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="sm:hidden">Quotes</span>
            <span className="hidden sm:inline">{t('sales.tabs.quotations', 'Sales Quotations')}</span>
          </button>
          <button
            onClick={() => handleTabChange('orders')}
            className={`snap-start flex-shrink-0 py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] ${
              activeTab === 'orders'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="sm:hidden">Orders</span>
            <span className="hidden sm:inline">{t('sales.tabs.orders', 'Sales Orders')}</span>
          </button>
          <button
            onClick={() => handleTabChange('delivery')}
            className={`snap-start flex-shrink-0 py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] ${
              activeTab === 'delivery'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('sales.tabs.delivery', 'Delivery')}
          </button>
        </nav>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-between items-stretch sm:items-center">
        {activeTab === 'delivery' ? (
          <SearchBar
            value={searchQuery}
            onChange={handleSearch}
            placeholder={t('sales.searchPlaceholder')}
          />
        ) : (
          <div className="hidden sm:block" />
        )}
        <div className="flex gap-2">
          <button
            onClick={() =>
              activeTab === 'quotations' ? sqRefetch() : activeTab === 'orders' ? soRefetch() : refetch()
            }
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
          >
            {t('common.refresh')}
          </button>
          {activeTab === 'quotations' ? (
            <button
              onClick={() => setShowSQDialog(true)}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 min-h-[44px]"
            >
              <span className="sm:hidden">+ Quote</span>
              <span className="hidden sm:inline">New Quotation</span>
            </button>
          ) : activeTab === 'orders' ? (
            <button
              onClick={() => setShowSODialog(true)}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 min-h-[44px]"
            >
              <span className="sm:hidden">+ Order</span>
              <span className="hidden sm:inline">New Order</span>
            </button>
          ) : (
            <button
              onClick={() => setIsAddingNew(true)}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 min-h-[44px]"
            >
              {t('sales.addSale')}
            </button>
          )}
        </div>
      </div>

      {/* SQ Dialog - Create */}
      {showSQDialog && (
        <SalesQuotationDialog onClose={() => setShowSQDialog(false)} onSuccess={() => sqRefetch()} />
      )}

      {/* SQ Dialog - Edit */}
      {editingSQ && (
        <SalesQuotationDialog editingSQ={editingSQ} onClose={() => setEditingSQ(null)} onSuccess={() => sqRefetch()} />
      )}

      {/* SO Dialog - Create */}
      {showSODialog && (
        <SalesOrderDialog onClose={() => setShowSODialog(false)} onSuccess={() => soRefetch()} />
      )}

      {/* SO Dialog - Edit */}
      {editingSO && (
        <SalesOrderDialog editingSO={editingSO} onClose={() => setEditingSO(null)} onSuccess={() => soRefetch()} />
      )}

      {/* Sales Quotations - Mobile Cards */}
      {activeTab === 'quotations' && (
        <div className="md:hidden space-y-3">
          {sqData?.sales_quotations?.map((sq) => {
            const expired = isQuotationExpired(sq.valid_until);
            const daysLeft = getDaysUntilExpiry(sq.valid_until);

            return (
              <MobileCard
                key={sq.sq_id}
                title={
                  sq.sheet_url ? (
                    <a href={sq.sheet_url} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline font-mono">
                      {sq.sq_number}
                    </a>
                  ) : (
                    <span className="font-mono">{sq.sq_number}</span>
                  )
                }
                subtitle={sq.customer?.chinese_name || sq.customer_code}
                status={
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${SQ_STATUS_COLORS[sq.status]}`}>
                    {SQ_STATUS_LABELS[sq.status]}
                  </span>
                }
                fields={[
                  { label: 'Quote Date', value: formatDate(sq.quotation_date) },
                  {
                    label: 'Valid Until',
                    value: (
                      <span className={expired ? 'text-red-600' : daysLeft <= 7 ? 'text-orange-600' : ''}>
                        {formatDate(sq.valid_until)}
                        {!expired && daysLeft <= 7 && <span className="ml-1 text-xs">({daysLeft}d)</span>}
                        {expired && <span className="ml-1 text-xs">(expired)</span>}
                      </span>
                    ),
                  },
                  { label: 'Total', value: formatCurrency(sq.subtotal, sq.currency), highlight: true },
                ]}
                actions={[
                  ...(!expired && (sq.status === 'draft' || sq.status === 'sent')
                    ? [{ label: 'Accept', onClick: () => handleAcceptQuotation(sq), variant: 'primary' as const }]
                    : []),
                  { label: 'Edit', onClick: () => setEditingSQ(sq), variant: 'secondary' as const },
                  { label: 'Delete', onClick: () => handleDeleteSQ(sq.sq_id), variant: 'danger' as const },
                ]}
              />
            );
          })}
          {(!sqData?.sales_quotations || sqData.sales_quotations.length === 0) && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No quotations found</div>
          )}
        </div>
      )}

      {/* Sales Quotations - Desktop Table */}
      {activeTab === 'quotations' && (
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Quotation No.
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Quote Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Valid Until
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Total
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
              {sqData?.sales_quotations?.map((sq) => {
                const expired = isQuotationExpired(sq.valid_until);
                const daysLeft = getDaysUntilExpiry(sq.valid_until);

                return (
                  <tr key={sq.sq_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                      {sq.sheet_url ? (
                        <a
                          href={sq.sheet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:underline"
                        >
                          {sq.sq_number}
                        </a>
                      ) : (
                        sq.sq_number
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {sq.customer?.chinese_name || sq.customer_code}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {formatDate(sq.quotation_date)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={
                          expired ? 'text-red-600' : daysLeft <= 7 ? 'text-orange-600' : 'text-gray-900 dark:text-white'
                        }
                      >
                        {formatDate(sq.valid_until)}
                      </span>
                      {!expired && daysLeft <= 7 && <span className="ml-1 text-xs text-orange-500">({daysLeft}d)</span>}
                      {expired && <span className="ml-1 text-xs text-red-500">(expired)</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(sq.subtotal, sq.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={sq.status}
                        onChange={(e) => handleUpdateSQStatus(sq.sq_id, e.target.value as SQStatus)}
                        className={`px-2 py-1 text-xs font-semibold rounded-full border-0 ${SQ_STATUS_COLORS[sq.status]}`}
                        disabled={sq.status === 'accepted'}
                      >
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                        <option value="accepted">Accepted</option>
                        <option value="rejected">Rejected</option>
                        <option value="expired">Expired</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {/* Accept button - only show for draft/sent status and not expired */}
                        {!expired && (sq.status === 'draft' || sq.status === 'sent') && (
                          <button
                            onClick={() => handleAcceptQuotation(sq)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Accept & Create SO"
                            disabled={acceptQuotationMutation.isLoading}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </button>
                        )}
                        {/* Show link to converted SO */}
                        {sq.converted_to_so_id && (
                          <span className="text-xs text-green-600" title={`Converted to SO: ${sq.converted_to_so_id}`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        )}
                        <button onClick={() => setEditingSQ(sq)} className="text-green-600 hover:text-green-800" title="Edit">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        {sq.sheet_url && (
                          <a
                            href={sq.sheet_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                            title="Open Sheet"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </a>
                        )}
                        <button onClick={() => handleDeleteSQ(sq.sq_id)} className="text-red-600 hover:text-red-800" title="Delete">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(!sqData?.sales_quotations || sqData.sales_quotations.length === 0) && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No quotations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Sales Orders - Mobile Cards */}
      {activeTab === 'orders' && (
        <div className="md:hidden space-y-3">
          {soData?.sales_orders?.map((so) => (
            <MobileCard
              key={so.so_id}
              title={
                <span className="flex items-center gap-1">
                  {so.sheet_url ? (
                    <a href={so.sheet_url} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline font-mono">
                      {so.so_number}
                    </a>
                  ) : (
                    <span className="font-mono">{so.so_number}</span>
                  )}
                  {so.source_sq_id && <span className="text-xs text-gray-400">(SQ)</span>}
                </span>
              }
              subtitle={so.customer?.chinese_name || so.customer_code}
              status={
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${SO_STATUS_COLORS[so.status]}`}>
                  {SO_STATUS_LABELS[so.status]}
                </span>
              }
              fields={[
                { label: 'Order Date', value: formatDate(so.order_date) },
                { label: 'Delivery', value: formatDate(so.delivery_date) },
                { label: 'Total', value: formatCurrency(so.grand_total, so.currency), highlight: true },
              ]}
              actions={[
                { label: 'Edit', onClick: () => setEditingSO(so), variant: 'secondary' as const },
                { label: 'Delete', onClick: () => handleDeleteSO(so.so_id), variant: 'danger' as const },
              ]}
            />
          ))}
          {(!soData?.sales_orders || soData.sales_orders.length === 0) && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No orders found</div>
          )}
        </div>
      )}

      {/* Sales Orders - Desktop Table */}
      {activeTab === 'orders' && (
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Order No.
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Order Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Delivery Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Grand Total
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
              {soData?.sales_orders?.map((so) => (
                <tr key={so.so_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                    {so.sheet_url ? (
                      <a
                        href={so.sheet_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:underline"
                      >
                        {so.so_number}
                      </a>
                    ) : (
                      so.so_number
                    )}
                    {so.source_sq_id && (
                      <span className="ml-1 text-xs text-gray-400" title={`From SQ: ${so.source_sq_id}`}>
                        (SQ)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                    {so.customer?.chinese_name || so.customer_code}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatDate(so.order_date)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatDate(so.delivery_date)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                    {formatCurrency(so.grand_total, so.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={so.status}
                      onChange={(e) => handleUpdateSOStatus(so.so_id, e.target.value as SOStatus)}
                      className={`px-2 py-1 text-xs font-semibold rounded-full border-0 ${SO_STATUS_COLORS[so.status]}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => setEditingSO(so)} className="text-purple-600 hover:text-purple-800" title="Edit">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      {so.sheet_url && (
                        <a
                          href={so.sheet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                          title="Open Sheet"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        </a>
                      )}
                      <button onClick={() => handleDeleteSO(so.so_id)} className="text-red-600 hover:text-red-800" title="Delete">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(!soData?.sales_orders || soData.sales_orders.length === 0) && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Sheet-based Sales - Mobile Cards */}
      {activeTab === 'delivery' && (
        <div className="md:hidden space-y-3">
          {data?.sales?.map((sale: ISale) => (
            <MobileCard
              key={sale.sale_id}
              title={<span className="font-mono">{sale.sale_id}</span>}
              subtitle={sale.customer}
              status={
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sale.status)}`}>
                  {sale.status}
                </span>
              }
              fields={[
                { label: t('sales.columns.date'), value: sale.date },
                { label: t('sales.columns.item'), value: sale.item },
                { label: t('sales.columns.quantity'), value: sale.quantity.toString() },
                { label: t('sales.columns.unitPrice'), value: `$${sale.unit_price.toFixed(2)}` },
                { label: t('sales.columns.total'), value: `$${sale.total.toFixed(2)}`, highlight: true },
              ]}
              actions={[
                { label: t('common.delete'), onClick: () => handleDelete(sale.sale_id), variant: 'danger' as const },
              ]}
            />
          ))}
          {(!data?.sales || data.sales.length === 0) && !isAddingNew && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('sales.noData')}</div>
          )}
        </div>
      )}

      {/* Sheet-based Sales - Desktop Table */}
      {activeTab === 'delivery' && (
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('sales.columns.id')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('sales.columns.date')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('sales.columns.customer')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('sales.columns.item')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('sales.columns.quantity')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('sales.columns.unitPrice')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('sales.columns.total')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('sales.columns.status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('sales.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
              {isAddingNew && (
                <tr className="bg-green-50">
                  <td className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">{t('common.auto')}</td>
                  <td className="px-4 py-2">
                    <input
                      type="date"
                      value={newSale.date || ''}
                      onChange={(e) => setNewSale({ ...newSale, date: e.target.value })}
                      className="w-full px-2 py-1 text-sm border rounded"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      placeholder={t('sales.columns.customer')}
                      value={newSale.customer || ''}
                      onChange={(e) => setNewSale({ ...newSale, customer: e.target.value })}
                      className="w-full px-2 py-1 text-sm border rounded"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      placeholder={t('sales.columns.item')}
                      value={newSale.item || ''}
                      onChange={(e) => setNewSale({ ...newSale, item: e.target.value })}
                      className="w-full px-2 py-1 text-sm border rounded"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      placeholder={t('common.qty')}
                      value={newSale.quantity || ''}
                      onChange={(e) => setNewSale({ ...newSale, quantity: parseInt(e.target.value) || 0 })}
                      className="w-20 px-2 py-1 text-sm border rounded"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      step="0.01"
                      placeholder={t('common.price')}
                      value={newSale.unit_price || ''}
                      onChange={(e) => setNewSale({ ...newSale, unit_price: parseFloat(e.target.value) || 0 })}
                      className="w-24 px-2 py-1 text-sm border rounded"
                    />
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">
                    ${((newSale.quantity || 0) * (newSale.unit_price || 0)).toFixed(2)}
                  </td>
                  <td className="px-4 py-2">
                    <select
                      value={newSale.status || 'pending'}
                      onChange={(e) => setNewSale({ ...newSale, status: e.target.value })}
                      className="px-2 py-1 text-sm border rounded"
                    >
                      <option value="pending">{t('status.pending')}</option>
                      <option value="completed">{t('status.completed')}</option>
                      <option value="cancelled">{t('status.cancelled')}</option>
                    </select>
                  </td>
                  <td className="px-4 py-2">
                    <button onClick={handleAddNew} disabled={createMutation.isLoading} className="text-green-600 hover:text-green-800 mr-2">
                      {t('common.save')}
                    </button>
                    <button
                      onClick={() => setIsAddingNew(false)}
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200"
                    >
                      {t('common.cancel')}
                    </button>
                  </td>
                </tr>
              )}
              {data?.sales?.map((sale: ISale) => (
                <tr key={sale.sale_id} className="hover:bg-gray-50 dark:bg-gray-700">
                  <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">{sale.sale_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{sale.date}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{sale.customer}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{sale.item}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{sale.quantity}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${sale.unit_price.toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">${sale.total.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sale.status)}`}>
                      {sale.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleDelete(sale.sale_id)}
                      disabled={deleteMutation.isLoading}
                      className="text-red-600 hover:text-red-800"
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
              {(!data?.sales || data.sales.length === 0) && !isAddingNew && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('sales.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination (delivery tab only) */}
      {activeTab === 'delivery' && data && data.total_pages > 1 && (
        <Pagination currentPage={page} totalPages={data.total_pages} onPageChange={setPage} />
      )}

      {/* Summary */}
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {activeTab === 'quotations'
          ? `${sqData?.sales_quotations?.length || 0} quotation(s)`
          : activeTab === 'orders'
          ? `${soData?.sales_orders?.length || 0} order(s)`
          : t('sales.showingSales', { count: data?.sales?.length || 0, total: data?.total_count || 0 })}
      </div>
    </div>
  );
};

export default SalesList;
